import ChevronRight from '@mui/icons-material/ChevronRight';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { type ReactElement, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import type { BlockedCause, DailyLoss, MeResponse, Money } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { Amount } from '../../components/Amount';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusChip } from '../../components/StatusChip';
import { formatClockTime } from '../shared/format-time';
import { formatBps } from '../shared/formatters';

import { linkForBlockedAction, linkForMissingStep } from './links';
import { rowDividerSx, sectionSx } from './styles';

type Tone = 'success' | 'neutral' | 'warning' | 'danger';

function toneColor(theme: Theme, tone: Tone): string {
  switch (tone) {
    case 'success':
      return theme.fx.success;
    case 'neutral':
      return theme.fx.textMuted;
    case 'warning':
      return theme.fx.warning;
    case 'danger':
      return theme.fx.danger;
  }
}

/** Card heading with the coloured state dot of the mockups; the words carry the state, not the dot. */
function StatusTitle({
  id,
  tone,
  children,
  aside,
}: {
  id: string;
  tone: Tone;
  children: ReactNode;
  aside?: ReactNode;
}): ReactElement {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box
        aria-hidden
        sx={(theme) => ({
          flex: 'none',
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: toneColor(theme, tone),
        })}
      />
      <Typography
        id={id}
        component="h2"
        sx={(theme) => ({
          fontWeight: 600,
          fontSize: 15,
          color: tone === 'danger' ? theme.fx.danger : 'text.primary',
        })}
      >
        {children}
      </Typography>
      {aside !== undefined && (
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: 13,
            color: 'text.secondary',
            minWidth: 0,
          }}
        >
          {aside}
        </Box>
      )}
    </Box>
  );
}

const overlineSx = { fontSize: 12, color: 'text.secondary', textTransform: 'uppercase' } as const;

/**
 * Overall account state at the top of Home (FXM-01 §5; M1 R1–R7, §4; mockups Main,
 * AccueilIncomplet, AccueilBloque). Every branch of `AccountStatus` is rendered.
 */
export function StatusCard({ me }: { readonly me: MeResponse }): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const titleId = useId();
  const missingTitleId = useId();
  const { status } = me;

  switch (status.status) {
    case 'operational':
    case 'suspended': {
      const operational = status.status === 'operational';
      return (
        <Paper variant="outlined" component="section" aria-labelledby={titleId} sx={cardSx}>
          <StatusTitle
            id={titleId}
            tone={operational ? 'success' : 'neutral'}
            aside={<AccountLine account={me.tradingAccount} />}
          >
            {t(operational ? 'home.status.operational' : 'home.status.suspended')}
          </StatusTitle>
          {!operational && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('home.status.suspendedHint')}
            </Typography>
          )}
          <AccountFigures
            equity={me.tradingAccount?.equity ?? null}
            dailyLoss={me.dailyLoss}
            reachable={me.tradingAccount?.connection === 'reachable'}
          />
        </Paper>
      );
    }
    case 'incomplete':
      // FXM-01 §5: Home always shows what is missing to be operational.
      return (
        <>
          <Paper
            variant="outlined"
            component="section"
            aria-labelledby={titleId}
            sx={(theme) => ({
              ...cardSx,
              gap: 1,
              bgcolor: theme.fx.warningBg,
              borderColor: theme.fx.warning,
              borderWidth: 1.5,
            })}
          >
            <StatusTitle id={titleId} tone="warning">
              {t('home.status.incomplete')}
            </StatusTitle>
            <Typography variant="body2" sx={(theme) => ({ color: theme.fx.warningText })}>
              {t('home.status.incompleteHint')}
            </Typography>
          </Paper>
          <Box component="section" sx={sectionSx}>
            <SectionHeader id={missingTitleId} title={t('home.missing.title')} />
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <List disablePadding aria-labelledby={missingTitleId}>
                {status.missingSteps.map((step, index) => (
                  <ListItem key={step} disablePadding sx={rowDividerSx}>
                    <ListItemButton
                      component={RouterLink}
                      to={linkForMissingStep(step, me.channels)}
                      sx={{ gap: 1.5, minHeight: 56 }}
                    >
                      <StepNumber value={index + 1} />
                      <Box component="span" sx={{ flex: 1, fontWeight: 600 }}>
                        {t(`home.missing.${step}`)}
                      </Box>
                      <ChevronRight aria-hidden sx={{ color: 'text.secondary' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </>
      );
    case 'blocked': {
      const since = earliestSince(status.causes);
      // FXM-01 §6 case 6: Home says what to do, one action per cause; the cause text is C2's (I12).
      return (
        <>
          <Paper
            variant="outlined"
            component="section"
            aria-labelledby={titleId}
            sx={(theme) => ({
              ...cardSx,
              gap: 1.25,
              bgcolor: theme.fx.dangerBg,
              borderColor: theme.fx.danger,
              borderWidth: 1.5,
            })}
          >
            <StatusTitle id={titleId} tone="danger">
              {t('home.status.blocked', { time: formatClockTime(since, language) })}
            </StatusTitle>
            {status.causes.map((cause) => (
              <Box
                key={cause.reasonCode}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
              >
                <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                  {t(`reason.${cause.reasonCode}`)}
                </Typography>
                <Button
                  component={RouterLink}
                  to={linkForBlockedAction(cause.action)}
                  variant="contained"
                  color="error"
                  sx={{ minHeight: 48, borderRadius: 3 }}
                >
                  {t(`home.blocked.${cause.action}`)}
                </Button>
              </Box>
            ))}
          </Paper>
          {me.openPositions.count > 0 && (
            <Paper variant="outlined" sx={{ p: 1.75, display: 'flex', gap: 1.5 }}>
              <InfoOutlined aria-hidden color="primary" />
              <Typography variant="body2">
                {t('home.blocked.positionsRemain', { count: me.openPositions.count })}
              </Typography>
            </Paper>
          )}
        </>
      );
    }
  }
}

const cardSx = { p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 } as const;

/** When copying actually stopped: the earliest cause (the schema guarantees at least one). */
function earliestSince(causes: readonly BlockedCause[]): string {
  return causes.map((cause) => cause.since).sort()[0] ?? '';
}

function StepNumber({ value }: { value: number }): ReactElement {
  return (
    <Box
      component="span"
      aria-hidden
      sx={(theme) => ({
        flex: 'none',
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        color: theme.fx.warningText,
        bgcolor: theme.fx.warningBg,
      })}
    >
      {value}
    </Box>
  );
}

/** "MT5 · server" and the demo badge (FXM-01 §6 case 4); no account number, no password (I9). */
function AccountLine({ account }: { account: MeResponse['tradingAccount'] }): ReactElement | null {
  const { t } = useTranslation();
  if (account === null) return null;
  return (
    <>
      <Box
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {t('home.status.accountLine', { platform: account.platform, server: account.server })}
      </Box>
      {account.isDemo && <StatusChip tone="info">{t('home.status.demo')}</StatusChip>}
    </>
  );
}

/** Equity and today's loss against the limit, with the gauge of mockup 1 (FXM-02 §4, §5; M1 R9). */
function AccountFigures({
  equity,
  dailyLoss,
  reachable,
}: {
  equity: Money | null;
  dailyLoss: DailyLoss | null;
  /** The account answers: with `dailyLoss` null, the loss is "not available" (M1 R20). */
  reachable: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const language = useLanguage();
  const notAvailable = dailyLoss === null && reachable;
  if (equity === null && dailyLoss === null && !notAvailable) return null;
  const limit = dailyLoss?.limit ?? null;
  const loss = dailyLoss === null ? null : formatBps(dailyLoss.lossBps, language);
  return (
    <>
      {/* C3 R7: on a narrow screen the daily loss wraps under the equity instead of drawing over it. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {equity !== null && (
          <Box sx={{ flex: 'none' }}>
            <Typography sx={overlineSx}>{t('home.status.equity')}</Typography>
            <Amount {...equity} fontSize={28} />
          </Box>
        )}
        {dailyLoss !== null && loss !== null && (
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Typography sx={overlineSx}>{t('home.dailyLoss.title')}</Typography>
            <Box sx={{ fontFamily: NUMERIC_FONT, fontSize: 15, whiteSpace: 'nowrap' }}>
              {limit === null
                ? loss
                : t('home.dailyLoss.ratio', { loss, limit: formatBps(limit.limitBps, language) })}
            </Box>
            <Amount {...dailyLoss.lossToday} fontSize={13} muted />
          </Box>
        )}
        {notAvailable && (
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Typography sx={overlineSx}>{t('home.dailyLoss.title')}</Typography>
            <Typography sx={{ fontSize: 14 }}>{t('home.dailyLoss.notAvailable')}</Typography>
          </Box>
        )}
      </Box>
      {notAvailable && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('home.dailyLoss.notAvailableHint')}
        </Typography>
      )}
      {dailyLoss !== null &&
        (limit === null ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('home.dailyLoss.noLimit')}
          </Typography>
        ) : (
          <LinearProgress
            variant="determinate"
            aria-label={t('home.dailyLoss.title')}
            value={Math.min(100, (dailyLoss.lossBps / limit.limitBps) * 100)}
            color={
              limit.state === 'reached'
                ? 'error'
                : limit.state === 'approached'
                  ? 'warning'
                  : 'success'
            }
            sx={(theme) => ({ height: 6, borderRadius: 3, bgcolor: theme.fx.neutralBg })}
          />
        ))}
    </>
  );
}
